import api from '@/api'
import client from '@/api/client'
import endpoints from '@/api/endpoints'
import {
  isNull,
  isUndefined,
  isEmpty,
  uniq,
  has,
  includes,
  isNumber,
  isEqual,
  snakeCase
} from 'lodash'
import qs from 'qs'


const nonMetricTableColumns = [
  'title', 'date', 'source',
  'urls', 'keywords', 'properties',
  'topics', 'is_sponsored'
]

const defaultTableColumns = () => [
  'title', 'source', 'urls',
  'date', 'page_views', 'social_actions',
  'avg_pct_consumed', 'conversions'
]

const requiredQueryLevelMetrics = [
  'page_views', 'social_actions', 'conversions',
  'avg_completion', 'avg_active_time'
]

const defaultState = () => ({
  page: 1,
  totalAssets: 0,
  assets: [],
  metrics: {},
  filters: {
    brandIds: [],
    keyword: '',
    assetSources: ['diffbot', 'nativo_advertiser'],
    distribution: 'all',
    language: '',
    domain: '',
    metricDateStart: '-10000',
    metricDateEnd: '0',
    createdDateStart: '-10000',
    createdDateEnd: '0',
    wordCount: [],
    customProperties: [],
    topics: [],
    keywords: [],
    collection: '',
    timeToConsume: [],
    views: [],
    socialActions: [],
    completionRate: [],
    conversionCount: {
      value: []
    },
    assetLevelMetrics: [
      'page_views', 'social_actions', 'avg_scroll_depth',
      'avg_pct_consumed', 'conversions'
    ],
    queryLevelMetrics: [],
    savedSearch: '',
    quickInsight: null
  },
  sort: {
    value: 'page_views',
    order: 'desc'
  },
  dynamicOptions: {
    domains: [],
    languages: [],
    topics: [],
    keywords: [],
    properties: [],
    savedSearches: [],
    quickInsights: []
  },
  optionLoading: {
    domains: false,
    topics: false,
    languages: false,
    keywords: false,
    properties: false,
    savedSearches: false
  },
  tableColumns: defaultTableColumns(),
  filterMetrics: {
    pageViews: {},
    socialActions: {},
    completionRate: {},
    conversions: {},
    duration: {},
    wordCount: {}
  },
  filterMetricsLoading: {
    pageViews: false,
    socialActions: false,
    completionRate: false,
    conversions: false,
    duration: false,
    wordCount: false
  }
})

const savedSearchRangeToFiltersRange = value => {
  const hasValues = !isEmpty(value) &&
    !isNull(value) &&
    !isUndefined(value) &&
    !isNull(value.gte) &&
    !isNull(value.gt) &&
    !isNull(value.lte) &&
    !isNull(value.lt) &&
    !isNull(value.eq)

  if (hasValues) {
    return [
      value.gte || value.gt || value.eq || 0,
      value.lte || value.lt || value.eq || 0,
    ]
  } else {
    return []
  }
}

const filterRangeToSavedSearchRange = value => {
  if (isNull(value) && !isEmpty(value)) {
    return {}
  } else {
    return {
      gte: value[0],
      lte: value[1]
    }
  }
}

const savedSearchToFilters = savedSearch =>
  Object.assign({}, defaultState().filters, {
    brandIds: savedSearch.brandIds
      .map(bid => String(bid)),
    keyword: savedSearch.phrase,
    assetSources: savedSearch.dataProviders
      .map(dp => dp.toLowerCase()),
    distribution: savedSearch.distribution || 'all',
    language: savedSearch.humanLanguages[0] || '',
    domain: savedSearch.domains[0] || '',
    metricDateStart: savedSearch.metricDateRange.gte || savedSearch.metricDateRange.gt,
    metricDateEnd: savedSearch.metricDateRange.lte || savedSearch.metricDateRange.lt,
    createdDateStart: savedSearch.createdDateRange.gte || savedSearch.createdDateRange.gt,
    createdDateEnd: savedSearch.createdDateRange.lte || savedSearch.createdDateRange.lt,
    wordCount: savedSearchRangeToFiltersRange(savedSearch.wordCountRange),
    customProperties: savedSearch.properties
      .map(p => `${p[0]}__${p[1]}`),
    topics: savedSearch.topics,
    keywords: savedSearch.keywords,
    collection: savedSearch.collection,
    timeToConsume: savedSearchRangeToFiltersRange(savedSearch.durationRange),
    views: savedSearchRangeToFiltersRange(savedSearch.pageViewsRange),
    socialActions: savedSearchRangeToFiltersRange(savedSearch.socialActionsRange),
    completionRate: savedSearchRangeToFiltersRange(savedSearch.completionRateRange),
    conversionCount: {
      value: savedSearchRangeToFiltersRange(savedSearch.conversionsRange)
    }
  })

const savedSearchToSort = savedSearch =>
  Object.assign({}, defaultState().sort, {
    value: savedSearch.fieldSortBy || savedSearch.metricSortBy || savedSearch.orderBy,
    order: savedSearch.sortDirection || savedSearch.orderDirection
  })

const stateToSavedSearch = ({ filters, sort }, name, brandId) => ({
  name,
  parentBrand: brandId,
  brandIds: filters.brandIds,
  assetIds: [],
  pageUrlIds: [],
  createdDateRange: {
    gte: filters.createdDateStart,
    lte: filters.createdDateEnd
  },
  dataProviders: filters.assetSources,
  properties: filters.customProperties
    .map(p => p.split('__')),
  topics: filters.topics,
  keywords: filters.keywords,
  collection: filters.collection || null,
  phrase: filters.keyword,
  distribution: filters.distribution,
  wordCountRange: filterRangeToSavedSearchRange(filters.wordCount),
  durationRange: filterRangeToSavedSearchRange(filters.timeToConsume),
  humanLanguages: !isEmpty(filters.language) ? [ filters.language ] : [],
  domains: !isEmpty(filters.domain) ? [ filters.domain ] : [],
  pageViewsRange: filterRangeToSavedSearchRange(filters.views),
  socialActionsRange: filterRangeToSavedSearchRange(filters.socialActions),
  completionRateRange: filterRangeToSavedSearchRange(filters.completionRate),
  conversionsRange: filterRangeToSavedSearchRange(filters.conversionCount.value),
  metricDateRange: {
    gte: filters.metricDateStart,
    lte: filters.metricDateEnd
  },
  orderDirection: sort.order,
  orderBy: sort.value,
  queryMetrics: uniq(
    filters.queryLevelMetrics
      .concat(requiredQueryLevelMetrics)
  ),
  assetMetrics: filters.assetLevelMetrics,
})

const mutations = {
  setFilterMetrics (state, { key, value }) {
    state.filterMetrics[key] = value
  },
  setFilterMetricsLoading (state, { key, value }) {
    state.filterMetricsLoading[key] = value
  },
  setAssets (state, data) { state.assets = data },
  addToAssets (state, data) { state.assets = state.assets.concat(data) },
  setTotalAssets (state, data) { state.totalAssets = data },
  setMetrics (state, data) { state.metrics = data },
  setSort (state, { key, value }) { state.sort[key] = value },
  setFilter (state, { key, value }) { state.filters[key] = value },
  setPage (state, value) { state.page = value },
  setFiltersFromObject (state, obj) {
    Object.keys(obj)
      .forEach(key => {
        if (has(state.filters, key)) {
          if (!isNull(state.filters[key]) && state.filters[key].constructor === Object) {
            state.filters[key] = Object.assign({}, state.filters[key], obj[key])
          } else {
            state.filters[key] = obj[key]
          }
        }
      })
  },
  setSortFromObject (state, obj) {
    Object.keys(obj)
      .forEach(key => {
        if (has(state.sort, key)) {
          state.sort[key] = obj[key]
        }
      })
  },
  setDynamicOption (state, { key, value }) { state.dynamicOptions[key] = value },
  setLoading (state, { key, value }) { state.optionLoading[key] = value },
  setFiltersFromSavedSearch (state, search) {
    const transformedSearch = savedSearchToFilters(search)

    Object.keys(transformedSearch)
      .forEach(key => {
        if (has(state.filters, key)) {
          if (!isNull(state.filters[key]) && state.filters[key].constructor === Object) {
            state.filters[key] = Object.assign({}, state.filters[key], transformedSearch[key])
          } else {
            state.filters[key] = transformedSearch[key]
          }
        }
      })
  },
  setSortFromSavedSearch (state, search) {
    const transformedSort = savedSearchToSort(search)

    Object.keys(transformedSort)
      .forEach(key => {
        if (has(state.sort, key)) {
          state.sort[key] = transformedSort[key]
        }
      })
  },
  setTableColumns (state, columns) {
    state.tableColumns = columns
  },
  setAssetMetricsFromColumns (state, selectedColumns) {
    state.filters.assetLevelMetrics = selectedColumns
      .filter(c => !includes(nonMetricTableColumns, c))
  },
  setQueryMetricsFromColumns (state, selectedColumns) {
    state.filters.queryLevelMetrics = selectedColumns
      .filter(c => !includes(nonMetricTableColumns, c))
  },
  resetStateToDefault (state) {
    Object.assign(state, defaultState())
  }
}

const actions = {
  setTableColumns ({ commit }, columns) {
    return new Promise((resolve, reject) => {
      try {
        commit('setTableColumns', columns)
        commit('setAssetMetricsFromColumns', columns)
        commit('setQueryMetricsFromColumns', columns)
        resolve()
      } catch (err) {
        reject(err)
      }
    })
  },
  resetFilters ({ commit }, brandId) {
    return new Promise((resolve, reject) => {
      const defaults = defaultState()
      try {
        commit('setFiltersFromObject', Object.assign({}, defaults.filters, { brandIds: [String(brandId)] }))
        commit('setSortFromObject', defaults.sort)
        resolve(true)
      } catch (err) {
        reject(err)
      }
    })
  },
  createSavedSearch ({ getters }, { name, brandId}) {
    const preparedData = Object.assign({}, getters.savedSearchParams, {
      name,
      parentBrand: brandId,
    })

    preparedData.properties = preparedData.properties.map(p => p.split('__'))

    return api.post(endpoints.savedSearch.list(), preparedData)
  },
  updateSavedSearch ({ state }, { pk, name, brandId }) {
    const preparedData = stateToSavedSearch(state, name, brandId)

    return api.patch(endpoints.savedSearch.detail(pk), preparedData)
  },
  getAssets ({ commit }, params) {
    return client.searchCreate(params)
      .then(resp => {
        commit('setAssets', resp.data.results)
        commit('setTotalAssets', resp.data.result_count)
      })
  },
  initExport (_context, params) {
    return client.searchExport(params)
  },
  getMoreAssets ({ commit }, params) {
    return client.searchCreate(params)
      .then(resp => {
        commit('addToAssets', resp.data.results)
        commit('setTotalAssets', resp.data.result_count)
      })
  },
  async getSearchTotals ({ commit }, params) {
    const resp = await client.searchTotalsCreate(params)
    commit('setMetrics', resp.data)
  },
  updateBrowserUrl (_context, params) {
    return new Promise((resolve) => {
      const newQS = qs.stringify(params, { strictNullHandling: true })
      const {
        protocol,
        host,
        pathname
      } = window.location
      const newUrl = `${protocol}//${host}${pathname}?${newQS}`
      window.history.replaceState({}, '', newUrl)
      resolve(true)
    })
  },
  async loadFilterMetrics ({ commit }, { metric, brandId }) {
    commit('setFilterMetricsLoading', { key: metric, value: true })

    const payload = {
      brand_ids: [ brandId ],
      metrics: [
        { metric: snakeCase(metric), agg: 'max' },
        { metric: snakeCase(metric), agg: 'avg' }
      ],
      breakdown: 'page_url_id',
      include_totals: true
    }

    const resp = await api.post(endpoints.metrics.create(), payload)

    commit('setFilterMetrics', {
      key: metric,
      value: resp.data.totals
    })
    commit('setFilterMetricsLoading', { key: metric, value: false })
  },
  loadDomainOptions ({ commit }, brandId) {
    return api.get(endpoints.brands.hostNames(brandId))
      .then(({ data }) => {
        const preparedData = data
          .map(({ value }) => ({ label: value, value }))
        commit('setDynamicOption', { key: 'domains', value: preparedData })
      })
  },
  loadSavedSearchOptions ({ commit }, brandId) {
    commit('setLoading', { key: 'savedSearches', value: true })

    const params = {
      parent_brand: brandId
    }

    return api.get(endpoints.savedSearch.list(), { params })
      .then(resp => {
        const preparedData = resp.data
          .map(item => ({ label: item.name, value: String(item.id), q: item }))
        commit('setDynamicOption', { key: 'savedSearches', value: preparedData })
      })
      .then(() => {
        commit('setLoading', { key: 'savedSearches', value: false })
      })
  },
  loadLanguageOptions ({ commit }, brandId) {
    return api.get(endpoints.brands.languages(brandId))
      .then(({ data }) => {
        const preparedData = data
          .map(item => ({ label: item[0], value: item[1] }))
        commit('setDynamicOption', { key: 'languages', value: preparedData })
      })
  },
  loadTopicOptions ({ commit }, params) {
    commit('setLoading', { key: 'topics', value: true })
    const preparedParams = Object.assign({}, params, {
      source: 'iab'
    })
    return api.get(endpoints.categories.list(), { params: preparedParams })
      .then(({ data }) => {
        const options = data
          .filter(category => isNumber(category.id) && category.subject !== '')
          .map(category => ({
            label: category.subject,
            value: category.subject,
            level: category.level,
            parent: category.parentSubject,
            assetCount: category.assetCount
          }))
        commit('setDynamicOption', { key: 'topics', value: options })
      })
      .then(() => { commit('setLoading', { key: 'topics', value: false }) })
  },
  loadKeywordOptions ({ commit }, params) {
    commit('setLoading', { key: 'keywords', value: true })
    return api.get(endpoints.keywords.list(), { params })
      .then(resp => {
        const options = resp.data.results
          .map(kw => ({
            label: kw.label,
            rightLabel: kw.docCount,
            value: kw.label
          }))
        commit('setDynamicOption', { key: 'keywords', value: options })
      })
      .then(() => { commit('setLoading', { key: 'keywords', value: false }) })
  },
  loadCustomPropertyOptions ({ commit }, params) {
    commit('setLoading', { key: 'properties', value: true })

    const preparedParams = Object.assign({}, params, { return_values: true })

    return api.get(endpoints.properties.list(), { params: preparedParams })
      .then(({ data }) => {
        const preparedData = data.map(item => {
          const preparedValues = item.values.map(v => ({
            label: v.value,
            value: `${item.name}__${v.value}`,
            rightLabel: v.assetCount
          }))

          return {
            label: item.name,
            value: item.name,
            children: preparedValues,
            clickable: false
          }
        })

        commit('setDynamicOption', { key: 'properties', value: preparedData })
      })
      .then(() => { commit('setLoading', { key: 'properties', value: false }) })
  },
  createCollection (_context, payload) {
    return api.post(endpoints.collections.list(), payload)
  },
  updateCollection (_context, { id, payload }) {
    return api.patch(endpoints.collections.detail(id), payload)
  },
  loadQuickInsightOptions ({ commit }) {
    return api.get(endpoints.quickInsights.list())
      .then(resp => {
        commit('setDynamicOption', { key: 'quickInsights', value: resp.data })
      })
  }
}


const topicToTreeNode = (t, topics) => ({
  value: t.label,
  label: t.value,
  rightLabel: t.assetCount,
  children: topics
    .filter(t2 => t2.parent === t.label)
    .map(t2 => topicToTreeNode(t2, topics))
})


const getters = {
  searchQueryParams: state => ({
    phrase: state.filters.keyword.length ? state.filters.keyword : null,
    brand_ids: state.filters.brandIds,
    order_by: state.sort.value,
    order_direction: state.sort.order,
    page: state.page,
    query_metrics: uniq(state.filters.queryLevelMetrics
      .concat(requiredQueryLevelMetrics)),
    asset_metrics: state.filters.assetLevelMetrics,
    word_count_range: state.filters.wordCount.length ? {
      gte: state.filters.wordCount[0],
      lte: state.filters.wordCount[1]
    } : null,
    duration_range: state.filters.timeToConsume.length ? {
      gte: state.filters.timeToConsume[0],
      lte: state.filters.timeToConsume[1]
    } : null,
    page_views_range: state.filters.views.length ? {
      gte: state.filters.views[0],
      lte: state.filters.views[1]
    } : null,
    social_actions_range: state.filters.socialActions.length ? {
      gte: state.filters.socialActions[0],
      lte: state.filters.socialActions[1]
    } : null,
    completion_rate_range: state.filters.completionRate.length ? {
      gte: isNull(state.filters.completionRate[0]) ? null : state.filters.completionRate[0] / 100,
      lte: isNull(state.filters.completionRate[1]) ? null : state.filters.completionRate[1] / 100
    } : null,
    conversions_range: state.filters.conversionCount.value.length ? {
      gte: state.filters.conversionCount.value[0],
      lte: state.filters.conversionCount.value[1]
    } : null,
    human_languages: state.filters.language.length ? [state.filters.language] : null,
    data_providers: state.filters.assetSources,
    distribution: state.filters.distribution,
    domains: state.filters.domain.length ? [state.filters.domain] : null,
    metric_date_range: {
      gte: state.filters.metricDateStart,
      lte: state.filters.metricDateEnd
    },
    created_date_range: {
      gte: state.filters.createdDateStart,
      lte: state.filters.createdDateEnd
    },
    collection: state.filters.collection && state.filters.collection.length ? state.filters.collection : null,
    properties: state.filters.customProperties &&
      state.filters.customProperties.length
        ? state.filters.customProperties
        : null,
    topics: state.filters.topics.length
      ? state.filters.topics
      : null,
    keywords: state.filters.keywords && state.filters.keywords.length
      ? state.filters.keywords
      : null,
    quick_insight: state.filters.quickInsight
  }),
  savedSearchParams: state => ({
    phrase: state.filters.keyword,
    brand_ids: state.filters.brandIds,
    order_by: state.sort.value,
    order_direction: state.sort.order,
    page: state.page,
    query_metrics: uniq(state.filters.queryLevelMetrics
      .concat(requiredQueryLevelMetrics)),
    asset_metrics: state.filters.assetLevelMetrics,
    word_count_range: state.filters.wordCount.length ? {
      gte: state.filters.wordCount[0],
      lte: state.filters.wordCount[1]
    } : {},
    duration_range: state.filters.timeToConsume.length ? {
      gte: state.filters.timeToConsume[0],
      lte: state.filters.timeToConsume[1]
    } : {},
    page_views_range: state.filters.views.length ? {
      gte: state.filters.views[0],
      lte: state.filters.views[1]
    } : {},
    social_actions_range: state.filters.socialActions.length ? {
      gte: state.filters.socialActions[0],
      lte: state.filters.socialActions[1]
    } : {},
    completion_rate_range: state.filters.completionRate.length ? {
      gte: state.filters.completionRate[0] / 100,
      lte: state.filters.completionRate[1] / 100
    } : {},
    conversions_range: state.filters.conversionCount.value.length ? {
      gte: state.filters.conversionCount.value[0],
      lte: state.filters.conversionCount.value[1]
    } : {},
    human_languages: state.filters.language.length ? [state.filters.language] : [],
    data_providers: state.filters.assetSources,
    distribution: state.filters.distribution,
    domains: state.filters.domain.length ? [state.filters.domain] : [],
    metric_date_range: {
      gte: state.filters.metricDateStart,
      lte: state.filters.metricDateEnd
    },
    created_date_range: {
      gte: state.filters.createdDateStart,
      lte: state.filters.createdDateEnd
    },
    collection: state.filters.collection && state.filters.collection.length ? state.filters.collection : null,
    properties: state.filters.customProperties,
    topics: state.filters.topics,
    keywords: state.filters.keywords
  }),
  browserQueryParams: ({ filters, sort }) => Object.assign({}, filters, { sort }),
  selectedFilterCount: state => {
    const defaultObj = defaultState().filters

    return Object.keys(defaultObj)
      .reduce((acc, key) => {
        if (key === 'brandIds' && state.filters[key].length < 2) { return acc }

        if (!isEqual(defaultObj[key], state.filters[key])) {
          return acc + 1
        }

        return acc
      }, 0)
  },
  propertyOptions: state => state.dynamicOptions.properties
    .reduce((acc, obj) => {
      return acc.concat(obj.children.map(child => Object.assign({}, child, {
        section: obj.label
      })))
    }, []),
  topicOptionsAsTree: ({ dynamicOptions: { topics } }) => {
    return topics
      .filter(t => isNull(t.parent))
      .map(t => topicToTreeNode(t, topics))
  },
  topicOptionsSearch: ({ dynamicOptions: { topics } }) => term => {
    return topics
      .filter(t => includes(t.label.toLowerCase(), term.toLowerCase()))
      .map(t => topicToTreeNode(t, topics))
  },
  optionLoading: state => optionType => state.optionLoading[optionType],
  defaultFilter: () => key => defaultState().filters[key]
}

export default {
  state: defaultState(),
  mutations,
  actions,
  getters,
  namespaced: true
}
